import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellRing, Trash2, X, RefreshCw, AlertCircle, Check, CheckCheck } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { IconButton } from '../design-system';

const NotificationIcon: React.FC = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [markAllConfirmation, setMarkAllConfirmation] = useState(false);
  const markAllTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const {
    isOpen,
    notifications,
    unreadCount,
    showBadgeNumber,
    totalCount,
    isLoading,
    hasMore,
    showUnreadOnly,
    deleteConfirmations,
    error,
    hasNotifications,
    dropdownRef,
    formatDate,
    handleToggleDropdown,
    handleScroll,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    handleDeleteClick,
    handleNotificationClick,
    handleToggleUnreadOnly,
    closeDropdown,
    refresh,
    user
  } = useNotifications();

  // Handle success state after refresh completes
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleRefresh = async () => {
    setShowSuccess(false);
    setIsRefreshing(true);
    
    try {
      await refresh();
      // Show success after refresh completes
      setIsRefreshing(false);
      setShowSuccess(true);
    } catch (err) {
      setIsRefreshing(false);
    }
  };

  const handleMarkAllRead = () => {
    if (markAllConfirmation) {
      // Second click - perform the action
      if (markAllTimeoutRef.current) {
        clearTimeout(markAllTimeoutRef.current);
      }
      markAllAsRead();
      setMarkAllConfirmation(false);
    } else {
      // First click - set confirmation state
      setMarkAllConfirmation(true);
      
      // Reset after 2 seconds
      markAllTimeoutRef.current = setTimeout(() => {
        setMarkAllConfirmation(false);
      }, 2000);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (markAllTimeoutRef.current) {
        clearTimeout(markAllTimeoutRef.current);
      }
    };
  }, []);

  const renderNotificationsList = (isMobile = false) => {
    // Show error state if there's an error
    if (error && !hasNotifications) {
      return (
        <div className={`text-center ${isMobile ? 'px-6 py-16' : 'px-4 py-8'}`}>
          <AlertCircle className={`${isMobile ? 'h-12 w-12' : 'h-8 w-8'} mx-auto mb-3 text-semantic-error opacity-50`} />
          <p className={`text-semantic-error ${isMobile ? 'text-sm' : 'text-xs'} mb-3`}>{error}</p>
          <button
            onClick={handleRefresh}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-ui-text-primary font-medium rounded-lg transition-colors ${isMobile ? 'text-sm' : 'text-xs'}`}
          >
            <RefreshCw className="w-3 h-3" />
            Try again
          </button>
        </div>
      );
    }
    
    if (notifications.length === 0 && !isLoading) {
      return (
        <div className={`text-center text-ui-text-tertiary ${isMobile ? 'px-6 py-16' : 'px-4 py-8'}`}>
          <Bell className={`${isMobile ? 'h-12 w-12' : 'h-8 w-8'} mx-auto mb-3 opacity-30`} />
          <p className={`${isMobile ? 'text-base' : 'text-sm'} font-medium mb-1`}>
            {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
          </p>
          <p className={`${isMobile ? 'text-sm' : 'text-xs'} opacity-70`}>
            {showUnreadOnly ? 'All caught up!' : "We'll notify you when something happens"}
          </p>
        </div>
      );
    }

    return (
      <>
        {/* Error banner at top if error but has notifications */}
        {error && hasNotifications && (
          <div className="bg-semantic-error/10 border-b border-semantic-error/30 px-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-semantic-error">Failed to load new notifications</p>
              <button
                onClick={handleRefresh}
                className="text-xs text-semantic-error hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-ui-border-primary">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${isMobile ? 'px-6 py-4' : 'px-4 py-3'} hover:bg-ui-background-tertiary transition-colors cursor-pointer ${
                !notification.read ? 'bg-brand-primary/10 dark:bg-brand-primaryDarkMode/10' : ''
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isMobile ? 'mt-2.5' : 'mt-1.5'} ${
                  !notification.read ? 'bg-brand-primary' : 'bg-transparent'
                }`} />
                <div className="flex-1 min-w-0">
                  <h4 className={`${isMobile ? 'text-base' : 'text-sm'} font-medium text-ui-text-primary ${!isMobile ? 'truncate' : ''}`}>
                    {notification.title}
                  </h4>
                  <p className={`${isMobile ? 'text-sm' : 'text-xs'} text-ui-text-secondary mt-0.5 ${!isMobile ? 'line-clamp-2' : ''}`}>
                    {notification.message}
                  </p>
                  <p className={`text-xs text-ui-text-tertiary mt-1`}>
                    {formatDate(notification.created_at)}
                  </p>
                </div>
                <div className={`flex ${isMobile ? 'flex-col gap-1' : 'gap-1'} flex-shrink-0`}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (notification.read) {
                        markAsUnread(notification.id);
                      } else {
                        markAsRead(notification.id);
                      }
                    }}
                    className={`${isMobile ? 'p-1.5' : 'p-1'} rounded-full transition-all duration-150 group ${
                      notification.read 
                        ? 'hover:bg-brand-primary/10 dark:hover:bg-brand-primaryDarkMode/10' 
                        : 'hover:bg-ui-background-tertiary'
                    } active:scale-95`}
                    title={notification.read ? "Mark as unread" : "Mark as read"}
                  >
                    <div className={`${isMobile ? 'w-3 h-3' : 'w-2 h-2'} rounded-full transition-all duration-150 ${
                      notification.read 
                        ? 'bg-neutral-300 dark:bg-neutral-600 group-hover:bg-brand-primary dark:group-hover:bg-brand-primaryDarkMode'
                        : 'bg-brand-primary group-hover:bg-brand-primaryDark'
                    }`} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(notification.id);
                    }}
                    className={`relative ${isMobile ? 'p-1.5' : 'p-1'} rounded-full transition-all hover:bg-semantic-error/10 text-ui-text-tertiary hover:text-semantic-error dark:text-ui-text-tertiary`}
                    title="Delete notification"
                  >
                    <Trash2 className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {notifications.length > 0 && notifications.length < 5 && (
          <div className="flex-1" style={{ minHeight: `${Math.max(0, (5 - notifications.length) * 30)}px` }} />
        )}
        
        {!hasMore && notifications.length === 0 && !isLoading && (
          <div className="p-4 text-center text-xs text-ui-text-tertiary">
            You're all caught up!
          </div>
        )}
      </>
    );
  };

  if (!user) return null;

  return (
    <>
      {/* Notification Icon Button */}
      <IconButton
        onClick={handleToggleDropdown}
        aria-label="Notifications"
        title="Notifications"
        icon={
          unreadCount > 0 ? (
            <BellRing size={16} className="animate-pulse" />
          ) : (
            <Bell size={16} />
          )
        }
        badge={
          unreadCount > 0 ? (
            <span
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--lime)',
                color: 'var(--lime-fg)',
                fontSize: 10,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid var(--bg-raised)',
              }}
            >
              {showBadgeNumber ? (unreadCount > 99 ? '99+' : unreadCount) : ''}
            </span>
          ) : undefined
        }
      />

      {/* Mobile Modal */}
      {isOpen && (
        <div className="sm:hidden fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => closeDropdown()}
          />
          
          <div 
            className="relative bg-ui-background-secondary rounded-2xl shadow-2xl overflow-hidden flex flex-col w-full max-w-md max-h-[90vh] min-h-[300px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-ui-border-primary bg-neutral-50/50 dark:bg-neutral-800/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-ui-text-primary">
                  Notifications
                </h3>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-lg bg-ui-background-tertiary p-0.5">
                    <button
                      onClick={(e) => { if (showUnreadOnly) handleToggleUnreadOnly(e); }}
                      className={`px-3 py-1 text-xs rounded-md transition-all ${
                        !showUnreadOnly 
                          ? 'bg-brand-primary text-ui-text-primary font-semibold shadow-sm' 
                          : 'text-ui-text-secondary hover:text-brand-primaryDark'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={(e) => { if (!showUnreadOnly) handleToggleUnreadOnly(e); }}
                      className={`px-3 py-1 text-xs rounded-md transition-all ${
                        showUnreadOnly 
                          ? 'bg-brand-primaryDarkMode text-ui-text-primary font-semibold shadow-sm' 
                          : 'text-ui-text-secondary hover:text-brand-primaryDarkMode'
                      }`}
                    >
                      Unread
                    </button>
                  </div>
                  <button
                    onClick={handleRefresh}
                    className={`relative p-1.5 transition-all duration-300 rounded-lg ${
                      isRefreshing
                        ? 'text-brand-primary dark:text-brand-primaryDarkMode'
                        : showSuccess
                        ? 'text-brand-primary dark:text-brand-primaryDarkMode'
                        : 'text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary hover:bg-ui-background-tertiary'
                    }`}
                    title="Refresh notifications"
                    disabled={isRefreshing}
                  >
                    {isRefreshing && (
                      <div className="absolute inset-0 rounded-lg bg-brand-primary/20 dark:bg-brand-primaryDarkMode/20 animate-pulse" />
                    )}
                    {showSuccess ? (
                      <Check className="h-4 w-4 relative z-10" />
                    ) : (
                      <RefreshCw className={`h-4 w-4 relative z-10 ${
                        isRefreshing ? 'animate-spin' : ''
                      }`} />
                    )}
                  </button>
                  <button
                    onClick={() => closeDropdown()}
                    className="p-1.5 text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary transition-colors rounded-lg hover:bg-ui-background-tertiary"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-ui-text-tertiary">
                  {totalCount === 0 ? 'No notifications' : 
                   showUnreadOnly ? (
                     unreadCount === 0 ? 'No unread' : `${unreadCount} unread`
                   ) : 
                   hasMore ? `${notifications.length} of total ${totalCount}` : !isLoading ? `All ${totalCount} notifications` : 'Loading...'}
                </p>
                <button
                  onClick={handleMarkAllRead}
                  disabled={unreadCount === 0}
                  className={`relative flex items-center gap-1.5 text-xs transition-all ${
                    unreadCount > 0
                      ? markAllConfirmation 
                        ? 'text-brand-primaryDark dark:text-brand-primaryDarkMode font-bold hover:text-brand-primary'
                        : 'text-brand-primaryDark dark:text-brand-primaryDarkMode hover:text-brand-primary'
                      : 'text-ui-text-tertiary cursor-not-allowed'
                  }`}
                  title={markAllConfirmation ? "Click again to confirm" : "Mark all notifications as read"}
                >
                  {markAllConfirmation && (
                    <div className="absolute -inset-1 rounded-md border-2 border-brand-primaryDark dark:border-brand-primaryDarkMode animate-pulse" />
                  )}
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>{markAllConfirmation ? 'Confirm' : 'Mark all read'}</span>
                </button>
              </div>
            </div>

            <div 
              className="flex-1 overflow-y-scroll [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-ui-background-tertiary [&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500"
              onScroll={handleScroll}
            >
              {renderNotificationsList(true)}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Dropdown */}
      {isOpen && (
        <div className="hidden sm:block absolute right-3 top-full mt-2 w-96 z-50" ref={dropdownRef}>
          <div className="bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary overflow-hidden">
            <div className="px-4 py-3 border-b border-ui-border-primary bg-neutral-50/50 dark:bg-neutral-800/30">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-ui-text-primary">
                  Notifications
                </h3>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded bg-ui-background-tertiary p-0.5">
                    <button
                      onClick={(e) => { if (showUnreadOnly) handleToggleUnreadOnly(e); }}
                      className={`px-2 py-0.5 text-xs rounded transition-all ${
                        !showUnreadOnly 
                          ? 'bg-brand-primary text-ui-text-primary font-semibold shadow-sm' 
                          : 'text-ui-text-secondary hover:text-brand-primaryDark'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={(e) => { if (!showUnreadOnly) handleToggleUnreadOnly(e); }}
                      className={`px-2 py-0.5 text-xs rounded transition-all ${
                        showUnreadOnly 
                          ? 'bg-brand-primaryDarkMode text-ui-text-primary font-semibold shadow-sm' 
                          : 'text-ui-text-secondary hover:text-brand-primaryDarkMode'
                      }`}
                    >
                      Unread
                    </button>
                  </div>
                  <button
                    onClick={handleRefresh}
                    className={`relative p-1 transition-all duration-300 rounded ${
                      isRefreshing
                        ? 'text-brand-primary dark:text-brand-primaryDarkMode'
                        : showSuccess
                        ? 'text-brand-primary dark:text-brand-primaryDarkMode'
                        : 'text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary hover:bg-ui-background-tertiary'
                    }`}
                    title="Refresh notifications"
                    disabled={isRefreshing}
                  >
                    {isRefreshing && (
                      <div className="absolute inset-0 rounded bg-brand-primary/20 dark:bg-brand-primaryDarkMode/20 animate-pulse" />
                    )}
                    {showSuccess ? (
                      <Check className="h-3.5 w-3.5 relative z-10" />
                    ) : (
                      <RefreshCw className={`h-3.5 w-3.5 relative z-10 ${
                        isRefreshing ? 'animate-spin' : ''
                      }`} />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-ui-text-tertiary">
                  {totalCount === 0 ? 'No notifications' : 
                   showUnreadOnly ? (
                     unreadCount === 0 ? 'No unread' : `${unreadCount} unread`
                   ) : 
                   hasMore ? `${notifications.length} of total ${totalCount}` : 
                   !isLoading ? `All ${totalCount} notifications` : 'Loading...'}
                </p>
                <button
                  onClick={handleMarkAllRead}
                  disabled={unreadCount === 0}
                  className={`relative flex items-center gap-1 text-xs transition-all ${
                    unreadCount > 0
                      ? markAllConfirmation 
                        ? 'text-brand-primaryDark dark:text-brand-primaryDarkMode font-bold hover:text-brand-primary'
                        : 'text-brand-primaryDark dark:text-brand-primaryDarkMode hover:text-brand-primary'
                      : 'text-ui-text-tertiary cursor-not-allowed'
                  }`}
                  title={markAllConfirmation ? "Click again to confirm" : "Mark all notifications as read"}
                >
                  {markAllConfirmation && (
                    <div className="absolute -inset-1 rounded border-2 border-brand-primaryDark dark:border-brand-primaryDarkMode animate-pulse" />
                  )}
                  <CheckCheck className="w-3 h-3" />
                  <span>{markAllConfirmation ? 'Confirm' : 'Mark all read'}</span>
                </button>
              </div>
            </div>

            <div 
              className="max-h-96 min-h-[200px] overflow-y-scroll [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-ui-background-tertiary [&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500"
              onScroll={handleScroll}
            >
              {renderNotificationsList()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationIcon;